import styled from '@emotion/styled';
import { useMemo } from 'react';
import SvgImage from '@/components/Module/SvgImage';
import { useFavoritesModal } from '@/components/Approval/Content/DocumentContents/Favorites/useFavoritesModal';
import { useAtom } from 'jotai';
import { documentFilterState } from '@/recoil/atom';
import { DocumentListFilters, SearchFields } from '@/types/api';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { modeToTitle } from '@/components/Approval/utils/functions';

const MAX_LENGTH = 77;

const Favorites = () => {
  const [filters] = useAtom(documentFilterState);
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const toCreatedBy = (createdBy: { passportId: string; name: string }[]) => {
    if (createdBy.length === 0) return '';
    const [first, ...rest] = createdBy;
    return `${t('작성자')}: ${first.name}${
      rest.length ? ` ${t('외')} ${rest.length}${t('명2')}` : ''
    }, `;
  };

  const toDocumentTypes = (documentTypes: string[]) => {
    if (documentTypes.length === 0) return '';
    const [first, ...rest] = documentTypes;
    return `${t('양식')}: ${t(modeToTitle(first))}${
      rest.length ? ` ${t('외')} ${rest.length}${t('개')}` : ''
    }, `;
  };

  const toKeyword = ({
    keyword,
    searchFiled,
  }: {
    keyword: string;
    searchFiled: SearchFields;
  }) => {
    if (keyword.length === 0) return '';

    const subjectStr = `${t('제목')}: ${keyword}, `;
    const contentStr = `${t('내용')}: ${keyword}, `;

    switch (searchFiled) {
      case 'subject,content':
        return `${subjectStr} ${contentStr}`;
      case 'subject':
        return `${subjectStr}`;
      case 'content':
        return `${contentStr}`;
    }
  };

  const useFiltersToContent = (filters: DocumentListFilters) => {
    return useMemo(() => {
      const parts = [
        toKeyword({
          keyword: filters.keyword,
          searchFiled: filters.searchFields,
        }),
        toCreatedBy(filters.createdBy),
        toDocumentTypes(filters.documentTypes),
      ];

      let result = parts
        .filter(Boolean)
        .join('')
        .replace(/[, ]+$/, '');

      if (result.length > MAX_LENGTH) {
        result = result.slice(0, MAX_LENGTH).replace(/[, ]+$/, '') + '...';
      }

      return result;
    }, [filters]);
  };

  const { openModal, FavoriteModal } = useFavoritesModal({
    defaultValue: useFiltersToContent(filters),
  });

  return (
    <>
      <StyledButton type={'button'} onClick={openModal}>
        <SvgImage name={'plus_12'} />
        <div>{t('내 필터로 저장')}</div>
      </StyledButton>
      {FavoriteModal}
    </>
  );
};
export default Favorites;

const StyledButton = styled.button`
  display: flex;
  padding: 8px 12px 8px 8px;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: 1px solid var(--dk-line, #e7e9ec);
  background: var(--dk-bg, #fff);
  font-size: 14px;
  line-height: 20px;
`;
